import * as React from "react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

type StepStatus = "completed" | "current" | "pending";

interface Step {
  id: string;
  label: string;
  description?: string;
  status: StepStatus;
}

interface StepIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  steps: Step[];
  orientation?: "horizontal" | "vertical";
  size?: "sm" | "md" | "lg";
}

const StepIndicator = React.forwardRef<HTMLDivElement, StepIndicatorProps>(
  ({ className, steps, orientation = "vertical", size = "md", ...props }, ref) => {
    const sizeClasses = {
      sm: { dot: "w-6 h-6", text: "text-xs", icon: "h-3 w-3" },
      md: { dot: "w-8 h-8", text: "text-sm", icon: "h-4 w-4" },
      lg: { dot: "w-10 h-10", text: "text-base", icon: "h-5 w-5" },
    };

    const sizes = sizeClasses[size];

    if (orientation === "horizontal") {
      return (
        <div ref={ref} className={cn("w-full", className)} {...props}>
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "rounded-full flex items-center justify-center font-medium transition-all duration-300",
                      sizes.dot,
                      step.status === "completed" && "progress-step-complete",
                      step.status === "current" && "progress-step-current",
                      step.status === "pending" && "progress-step-pending"
                    )}
                  >
                    {step.status === "completed" ? (
                      <Check className={sizes.icon} />
                    ) : (
                      <span className={sizes.text}>{index + 1}</span>
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <p
                      className={cn(
                        "font-medium",
                        sizes.text,
                        step.status === "completed" && "text-emerald-700",
                        step.status === "current" && "text-[hsl(var(--color-4))]",
                        step.status === "pending" && "text-[hsl(var(--color-8))]"
                      )}
                    >
                      {step.label}
                    </p>
                    {step.description && (
                      <p className="text-xs text-[hsl(var(--color-8))] mt-0.5">
                        {step.description}
                      </p>
                    )}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      "flex-1 h-0.5 mx-4",
                      step.status === "completed"
                        ? "bg-emerald-500"
                        : "bg-[hsl(var(--color-3))]"
                    )}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div ref={ref} className={cn("relative", className)} {...props}>
        {steps.map((step, index) => (
          <div key={step.id} className="relative flex gap-4 pb-8 last:pb-0">
            {/* Vertical line */}
            {index < steps.length - 1 && (
              <div
                className={cn(
                  "absolute left-4 top-8 bottom-0 w-0.5 -translate-x-1/2",
                  step.status === "completed"
                    ? "bg-emerald-500"
                    : "bg-[hsl(var(--color-3))]"
                )}
              />
            )}

            {/* Step dot */}
            <div
              className={cn(
                "relative z-10 rounded-full flex items-center justify-center font-medium flex-shrink-0 transition-all duration-300",
                sizes.dot,
                step.status === "completed" && "progress-step-complete",
                step.status === "current" && "progress-step-current",
                step.status === "pending" && "progress-step-pending"
              )}
            >
              {step.status === "completed" ? (
                <Check className={sizes.icon} />
              ) : step.status === "current" ? (
                <div className="w-2 h-2 rounded-full bg-white" />
              ) : (
                <span className={sizes.text}>{index + 1}</span>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 pt-1">
              <p
                className={cn(
                  "font-semibold",
                  sizes.text,
                  step.status === "completed" && "text-emerald-700",
                  step.status === "current" && "text-[hsl(var(--color-4))]",
                  step.status === "pending" && "text-[hsl(var(--color-8))]"
                )}
              >
                {step.label}
              </p>
              {step.description && (
                <p className="text-sm text-[hsl(var(--color-8))] mt-1">
                  {step.description}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }
);
StepIndicator.displayName = "StepIndicator";

export { StepIndicator };
export type { StepIndicatorProps, Step, StepStatus };
